const API_BASE = '/api';

class ApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  private async request(endpoint: string, options: RequestInit = {}) {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(`${API_BASE}/${endpoint}`, {
        ...options,
        headers,
        signal: AbortSignal.timeout(30000), // 30 second timeout
      });

      const data = await response.json();

      if (!response.ok) {
        // Enhanced error messages based on status codes
        const errorMessage = data.error || this.getErrorMessage(response.status, endpoint);
        const error = new Error(errorMessage) as any;
        error.status = response.status;
        error.data = data;
        throw error;
      }

      return data;
    } catch (error: any) {
      // Network errors or timeout
      if (error.name === 'AbortError' || error.name === 'TimeoutError') {
        throw new Error('Request timed out. Please check your connection and try again.');
      }
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Unable to connect to the server. Please check your internet connection.');
      }
      // Re-throw API errors with enhanced messages
      throw error;
    }
  }

  private getErrorMessage(status: number, endpoint: string): string {
    const errorMessages: Record<number, string> = {
      400: 'Invalid request. Please check your input and try again.',
      401: 'Authentication required. Please log in again.',
      403: 'You do not have permission to access this resource.',
      404: 'The requested resource was not found.',
      409: 'This action conflicts with existing data. Please refresh and try again.',
      429: 'Too many requests. Please wait a moment before trying again.',
      500: 'Server error. Our team has been notified. Please try again later.',
      502: 'Service temporarily unavailable. Please try again in a moment.',
      503: 'Service is undergoing maintenance. Please try again shortly.',
    };

    return errorMessages[status] || `An unexpected error occurred (${status}). Please try again.`;
  }

  // Auth endpoints
  async login(email: string) {
    return this.request('auth/login', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async register(email: string, name: string, role: 'client' | 'pt', pt_id?: string) {
    return this.request('auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, name, role, pt_id }),
    });
  }

  // User endpoints
  async getUser(userId?: string) {
    return this.request(userId ? `users/${userId}` : 'users');
  }

  async getClients() {
    return this.request('pt/clients');
  }

  // Workout endpoints
  async getWorkouts(clientId?: string) {
    const params = clientId ? `?client_id=${clientId}` : '';
    return this.request(`workouts${params}`);
  }

  async createWorkout(workout: any) {
    return this.request('workouts', {
      method: 'POST',
      body: JSON.stringify(workout),
    });
  }

  // Chat endpoints
  async getMessages(chatId: string) {
    return this.request(`chat?chat_id=${chatId}`);
  }

  async sendMessage(chatId: string, text: string, senderId?: string) {
    return this.request('chat', {
      method: 'POST',
      body: JSON.stringify({ chat_id: chatId, sender_id: senderId || 'user1', text }),
    });
  }

  // AI Chat endpoint
  async sendAIMessage(messages: any[], workouts: any[]) {
    return this.request('ai-chat', {
      method: 'POST',
      body: JSON.stringify({ messages, workouts }),
    });
  }
}

export const api = new ApiClient();